<template>
  <div class="vue-select" :data-test="dataTest">
    <label v-if="label" class="custom_input_label">{{ label }}</label>
    <v-menu
      ref="menu"
      :close-on-content-click="!multiple"
      :transition="false"
      max-height="200"
      offset-y
    >
      <template #activator="{ on, attrs }">
        <ValidationProvider
          v-slot="{ errors }"
          :name="name || label"
          :rules="rules"
          class="vue-select__wrap"
        >
          <div class="vue-select__input">
            <input
              ref="input"
              :class="[
                'vue-select__input',
                { 'vue-select__input-disabled': disabled },
              ]"
              :disabled="disabled"
              :value="selectionText()"
              type="text"
              :placeholder="placeholder"
              v-bind="attrs"
              @blur="onBlur"
              @focus="onFocus"
              @input="searchInputHandler($event.target.value)"
              v-on="disabled ? {} : on"
            />
            <div v-if="prependIcon" class="vue-select__prepend-icon">
              <vue-icon :icon="prependIcon" />
            </div>
            <div
              :style="{ pointerEvents: value ? 'initial' : 'none' }"
              class="vue-select__icon"
            >
              <v-progress-circular
                v-show="loading"
                class="vue-select__loader vue-select__loader-input"
                indeterminate
                size="18"
                width="3"
              ></v-progress-circular>
              <div class="vue-select__icon__indicator">
                <v-icon
                  v-show="!loading && (!clearable || !hasValue)"
                  :class="{ 'vue-select__icon__indicator-open': isOpenedMenu }"
                  >mdi-menu-down
                </v-icon>
              </div>
              <v-btn
                v-if="hasValue && clearable && !loading"
                icon
                small
                @click="clear"
              >
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </div>
          </div>
          <div v-if="errors[0]" class="px-3 v-messages__message mt-1">
            <span class="error--text v-messages">{{ errors[0] }}</span>
          </div>
        </ValidationProvider>
      </template>
      <v-list ref="list" :data-test="dataTest" dense>
        <v-list-item
          v-for="(item, index) in items"
          :key="index"
          :class="[
            {
              'v-list-item--active v-list-item--link': isActive(item),
            },
          ]"
          active
          ripple
          @click="changeValue(item)"
        >
          <slot :item="item" :searchText="searchText" name="option">
            <v-list-item-title>{{ getItemText(item) }}</v-list-item-title>
          </slot>
        </v-list-item>
        <v-list-item v-if="$scopedSlots['append:item']">
          <slot :searchText="searchText" name="append:item"></slot>
        </v-list-item>
        <v-list-item v-if="items.length === 0 && !loading">
          <slot :searchText="searchText" name="no-data">
            <v-list-item-title>{{ $t("noDataAvailable") }}</v-list-item-title>
          </slot>
        </v-list-item>
        <div class="py-1 text-center">
          <span
            ref="load"
            class="vue-select__loader vue-select__loader-list d-inline-block"
          ></span>
        </div>
      </v-list>
    </v-menu>
  </div>
</template>

<script>
import "./vue-select.scss";
import { debounce } from "@/utils/pureFunctions";
// import i18n from "@/plugins/I18n";
// import { capitalize } from "vuetify-loader/lib/util";

export default {
  props: {
    prependIcon: {
      type: String,
    },
    placeholder: {
      type: String,
    },
    dataTest: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    rules: {},
    name: {
      type: String,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    infinite: {
      type: Boolean,
      default: false,
    },
    value: {},
    items: {
      type: Array,
      default: () => [],
    },
    itemText: {
      type: [Function, String],
      default: "name",
    },
    itemValue: {
      type: [Function, String],
      default: "id",
    },
    getItemPromise: {
      type: Function,
    },
  },
  data() {
    return {
      observer: null,
      page: 0,
      searchText: null,
      selectedItems: [],
      cachedItems: {},
      lastPage: null,
      isOpenedMenu: false,
      loading: false,
    };
  },
  methods: {
    reset() {
      this.lastPage = null;
      this.page = 0;
      this.cachedItems = {};
    },
    isActive(item) {
      const value = this.getItemValue(item);
      if (this.multiple && Array.isArray(this.value)) {
        return this.value.indexOf(value) > -1;
      } else {
        return value === this.value;
      }
    },
    onSearch: debounce(async function (val) {
      if (this.getItemPromise) {
        try {
          this.page = 0;
          this.loading = true;
          const { data, lastPage, total } = await this.getItemPromise({
            text: val,
            page: this.page,
          });
          this.page = 1;
          if (typeof lastPage !== "undefined") {
            this.lastPage = lastPage;
          }
          if (total) {
            this.total = total;
          }
          this.$emit("item-resolved", data);
        } catch (e) {
          console.error(e);
        } finally {
          this.loading = false;
        }
      } else {
        this.$emit("search", val);
      }
      this.searchText = val;
    }, 300),
    clear() {
      this.searchText = null;
      this.onSearch();
      if (this.multiple) {
        this.$emit("input", []);
      } else {
        this.$emit("input");
      }
    },
    async infiniteScroll([{ isIntersecting, target }]) {
      if (
        isIntersecting &&
        this.infinite &&
        !this.loading &&
        (this.lastPage === null || this.lastPage > this.page)
      ) {
        if (this.getItemPromise) {
          try {
            const ul = target.offsetParent;
            const scrollTop = target.offsetParent.scrollTop;
            try {
              this.loading = true;
              let { data, lastPage } = await this.getItemPromise({
                page: this.page,
                text: this.searchText,
              });
              this.$emit(
                "next-page-resolved",
                data.filter((item) => {
                  return !(this.getItemValue(item) in this.cachedItems);
                })
              );
              if (lastPage) {
                this.lastPage = lastPage;
              }
              this.page++;
            } catch (e) {
              console.error(e);
            } finally {
              this.loading = false;
            }
            await this.$nextTick();
            ul.scrollTop = scrollTop;
          } catch (e) {
            console.error(e);
          }
        }
      }
    },
    setCachedItems(data) {
      this.cachedItems = data.reduce((int, item) => {
        int[this.getItemValue(item)] = item;
        return int;
      }, {});
    },
    selectionText() {
      if (this.selectedItemText && !this.isOpenedMenu) {
        return this.selectedItemText;
      }
      return this.searchText;
    },
    onFocus(e) {
      this.$emit("focus", e);
      this.isOpenedMenu = true;
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.$refs.load) this.observer.observe(this.$refs.load);
        }, 320);
      });
    },
    onBlur() {
      this.$emit("blur");
      this.$nextTick(() => {
        this.isOpenedMenu = false;
      });
      setTimeout(() => {
        const { list } = this.$refs;
        if (list) {
          list.$el.parentElement.scrollTop = 0;
        }
        if (this.searchText) {
          this.searchText = null;
          if (!this.value) {
            this.onSearch(null);
          }
        }
      }, 300);
      this.observer.disconnect();
    },
    searchInputHandler(val) {
      this.searchText = val;
      this.onSearch(val);
      this.$emit("changeText", val);
      this.$emit("changeText2", val);
    },
    changeValue(item) {
      if (this.multiple) {
        if (!Array.isArray(this.value)) {
          this.$emit("input", [this.getItemValue(item)]);
          this.$emit("CHANGE_VALUE", item);
        } else {
          const selectedItemIndex = this.value.indexOf(this.getItemValue(item));
          if (selectedItemIndex === -1) {
            this.value.push(this.getItemValue(item));
          } else {
            this.value.splice(selectedItemIndex, 1);
          }
          this.$emit("input", this.value);
        }
      } else {
        const selectedValue = this.getItemValue(item);

        if (selectedValue !== this.value) {
          this.$emit("input", selectedValue);
          this.$emit("CHANGE_VALUE", item);
        }
      }
    },
    getItemText(item) {
      if (typeof item === "string" || typeof item === "number") {
        return item;
      } else if (typeof this.itemText === "string") {
        return item[this.itemText];
      } else if (typeof this.itemText === "function") {
        return this.itemText(item);
      } else {
        return item;
      }
    },
    getItemValue(item) {
      if (typeof item === "string" || typeof item === "number") {
        return item;
      } else if (typeof this.itemValue === "string") {
        return item[this.itemValue];
      } else if (typeof this.itemValue === "function") {
        return this.itemValue(item);
      } else {
        return item;
      }
    },
  },
  computed: {
    hasValue() {
      if (this.multiple) return this.value && !!this.value.length;
      return !!this.value;
    },
    selectedItem() {
      if (this.multiple) {
        return this.items.filter((item) => {
          return this.value && this.value.indexOf(this.getItemValue(item)) > -1;
        });
      } else {
        if (!this.value) return null;
        return this.items.find(
          (item) => this.getItemValue(item) === this.value
        );
      }
    },
    selectedItemText() {
      if (!this.selectedItem) return null;
      if (this.multiple) {
        return this.selectedItem
          .map((item) => this.getItemText(item))
          .join(", ");
      } else {
        return this.getItemText(this.selectedItem);
      }
    },
  },
  mounted() {
    this.observer = new IntersectionObserver(this.infiniteScroll);
  },
};
</script>

<style scoped>
.vue-select {
  min-width: 100px;
}
</style>
